/**
 * Booth coupon — DOM wiring (Kyle, 2026-07-29).
 *
 * The card works with NO JavaScript: its button is a plain link to Shopify's
 * `/discount/<CODE>?redirect=` route. This file replaces that with the better path —
 * `POST /cart/update.js`, which applies the code WITHOUT moving the page — and adds
 * the automatic half plus copy-to-clipboard.
 *
 * The decisions live in eg-coupon-state.js with unit tests. Two rules carry the
 * weight, both from measurements on 2026-07-29 recorded there: never announce a
 * discount that is not really on the cart, and never auto-apply over a code the
 * shopper already holds (applying REPLACES).
 */
import { cartUpdateBody, couponStatus, decideAutoApply } from './eg-coupon-state.js';

(function () {
  const root = document.querySelector('[data-eg-coupon]');
  if (!root) return;

  const code = (root.dataset.egCoupon || '').trim();
  if (!code) return;

  const auto = root.hasAttribute('data-eg-coupon-auto');
  const button = root.querySelector('[data-eg-coupon-apply]');
  const copyBtn = root.querySelector('[data-eg-coupon-copy]');

  const json = { 'Content-Type': 'application/json' };

  const readCart = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);

  const applyCode = () =>
    fetch('/cart/update.js', {
      method: 'POST',
      headers: json,
      body: JSON.stringify(cartUpdateBody({ code })),
    })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);

  /**
   * Publish what is TRUE, from the cart, onto the element — never from "we just sent
   * a request". CSS keys off the state, so an unknown/failed read simply shows the
   * plain card rather than a claim.
   */
  const publish = (cart) => {
    const state = cart ? couponStatus({ cart, code }) : 'absent';
    root.setAttribute('data-eg-coupon-state', state);
    return state;
  };

  (async () => {
    let cart = await readCart();
    publish(cart);

    if (auto && decideAutoApply({ cart, code })) {
      // Idempotent, and it cannot strand anyone — no navigation is involved.
      const updated = await applyCode();
      publish(updated || (await readCart()));
    }
  })();

  /* -------------------------------------------------------------------- the button
   *
   * Progressive enhancement: the markup ships a real link to /discount/<CODE>, so it
   * works with this file blocked. With the file running, the click is handled in place
   * — no page load, and the shopper stays where they were reading.
   */
  if (button) {
    button.addEventListener('click', async (event) => {
      event.preventDefault();
      const original = button.querySelector('.eg-btn__label');
      const wasLabel = original ? original.textContent : '';
      if (original && button.dataset.egCouponApply) {
        original.textContent = button.dataset.egCouponApply;
      }

      const updated = await applyCode();
      const state = publish(updated || (await readCart()));

      // Restore the label unless the state now speaks for itself.
      if (original && state !== 'applied' && state !== 'saved') {
        original.textContent = wasLabel;
      }
    });
  }

  /* -------------------------------------------------------------------- copying */

  if (copyBtn && navigator.clipboard) {
    const label = copyBtn.querySelector('.eg-btn__label');
    const original = label ? label.textContent : '';
    copyBtn.hidden = false;
    copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(code);
        if (label) label.textContent = copyBtn.dataset.egCouponCopy || 'Copied';
        // The label is the only feedback, so it has to return or the button stops
        // looking like something you can press again.
        setTimeout(() => {
          if (label) label.textContent = original;
        }, 2000);
      } catch {
        // Clipboard denied (permissions, insecure context). The code is on screen to
        // be read, so nothing needs announcing.
      }
    });
  }
})();
